import { useState, useEffect } from "react";
import axios, { API } from "../../api";

const REWARD_LABELS = {
  extended_trial: "Extended trial",
  free_base: "Free Base tier ($4.99)",
  free_premium_month: "1+ free month of Premium",
  free_topup_minutes: "Free Aura voice minutes",
  free_creator_studio_month: "Free Creator Studio months",
  percent_off: "% off at checkout (Stripe discount)",
};

const PREMIUM_TIER_OPTIONS = [
  { slug: "spark",    label: "Spark ($3.49/mo)" },
  { slug: "efflora",  label: "Efflora ($6.99/mo)" },
  { slug: "aurora",   label: "Aurora ($12.99/mo)" },
  { slug: "eternal",  label: "Eternal ($25.00/mo)" },
];
const TIER_NAMES = { spark: "Spark", efflora: "Efflora", aurora: "Aurora", eternal: "Eternal" };
const TOPUP_MINUTE_OPTIONS = [30, 60, 120, 240];
const CREATOR_STUDIO_MONTH_OPTIONS = [1, 3, 6, 12];

const PERCENT_APPLIES_OPTIONS = [
  { slug: "all", label: "All purchases (everything in Aura)" },
  { slug: "subscriptions", label: "Subscription tiers only (Spark/Efflora/Aurora/Eternal)" },
];

/**
 * Admin-only modal: create + manage promo codes.
 * Only visible when user.is_admin === true.
 */
export function AdminPromosModal({ onClose }) {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedCode, setExpandedCode] = useState(null);
  const [redemptions, setRedemptions] = useState({}); // { code: [{...}] }
  const [copiedCode, setCopiedCode] = useState(null);

  // Create form
  const [mode, setMode] = useState("auto"); // auto | custom
  const [customCode, setCustomCode] = useState("");
  const [rewardType, setRewardType] = useState("extended_trial");
  const [trialDays, setTrialDays] = useState(30);
  const [freeMonthTier, setFreeMonthTier] = useState("spark");
  const [premiumMonths, setPremiumMonths] = useState(1); // 1, 3, 6, or 12
  const [topupMinutes, setTopupMinutes] = useState(30); // 30, 60, 120, 240
  const [creatorStudioMonths, setCreatorStudioMonths] = useState(1); // 1, 3, 6, 12
  const [percentOff, setPercentOff] = useState(20); // Phase 30.W — 1-99
  const [percentAppliesTo, setPercentAppliesTo] = useState("all"); // all | subscriptions
  const [maxUses, setMaxUses] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [lastCreatedCode, setLastCreatedCode] = useState(""); // confirmation banner

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/promos`);
      setPromos(res.data.promos || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setCreateError("");
    // Safety net: if user typed a custom code but is on Auto tab, honor the typed code
    // (their intent was clearly "use this name"). Without this, the typed value is silently
    // discarded and the backend auto-generates AURA-XXXX-XXXX — leading to "code isn't valid"
    // confusion when they try to share/redeem the name they typed.
    const effectiveMode = customCode.trim() ? "custom" : mode;
    if (mode === "custom" && !customCode.trim()) {
      setCreateError("Enter a code name, or switch to Auto-generate.");
      return;
    }
    setCreating(true);
    const body = {
      reward_type: rewardType,
      note: note.trim() || undefined,
    };
    if (effectiveMode === "custom" && customCode.trim()) body.code = customCode.trim();
    if (rewardType === "extended_trial") body.value = parseInt(trialDays, 10) || 30;
    if (rewardType === "free_premium_month") {
      body.tier = freeMonthTier;
      body.months = parseInt(premiumMonths, 10) || 1;
    }
    if (rewardType === "free_topup_minutes") {
      body.topup_minutes = parseInt(topupMinutes, 10) || 30;
    }
    if (rewardType === "free_creator_studio_month") {
      body.creator_studio_months = parseInt(creatorStudioMonths, 10) || 1;
    }
    if (rewardType === "percent_off") {
      body.percent = parseInt(percentOff, 10) || 0;
      body.applies_to = percentAppliesTo;
    }
    if (maxUses) body.max_uses = parseInt(maxUses, 10);
    if (expiresAt) body.expires_at = expiresAt;
    try {
      const res = await axios.post(`${API}/admin/promos`, body);
      const savedCode = res?.data?.code || body.code || "(auto-generated)";
      setLastCreatedCode(savedCode);
      // reset + reload
      setCustomCode(""); setNote(""); setMaxUses(""); setExpiresAt("");
      setCreateOpen(false);
      load();
      // Auto-clear the success banner after a few seconds so it doesn't linger
      setTimeout(() => setLastCreatedCode(""), 8000);
    } catch (e) {
      setCreateError(e?.response?.data?.detail || "Failed to create code");
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (code) => {
    try {
      await axios.patch(`${API}/admin/promos/${code}`);
      load();
    } catch (e) { /* noop */ }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete code ${code}? Existing redemptions stay for audit.`)) return;
    try {
      await axios.delete(`${API}/admin/promos/${code}`);
      load();
    } catch (e) { /* noop */ }
  };

  const handleExpand = async (code) => {
    if (expandedCode === code) {
      setExpandedCode(null);
      return;
    }
    setExpandedCode(code);
    if (!redemptions[code]) {
      try {
        const res = await axios.get(`${API}/admin/promos/${code}/redemptions`);
        setRedemptions((r) => ({ ...r, [code]: res.data.redemptions || [] }));
      } catch (e) { /* noop */ }
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 1500);
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="admin-promos-modal" onClick={(e) => e.stopPropagation()} data-testid="admin-promos-modal">
        <div className="journal-header">
          <h2>🎟️ Promo Codes</h2>
          <button className="journal-close" onClick={onClose} data-testid="admin-promos-close">×</button>
        </div>

        <button
          className="init-btn"
          onClick={() => setCreateOpen(!createOpen)}
          data-testid="admin-create-toggle-btn"
          style={{ marginBottom: "1rem" }}
        >
          {createOpen ? "Cancel" : "+ New Code"}
        </button>

        {lastCreatedCode && (
          <div
            data-testid="admin-create-success-banner"
            style={{
              background: "rgba(52, 211, 153, 0.15)",
              border: "1px solid rgba(52, 211, 153, 0.45)",
              borderRadius: "8px",
              padding: "0.6rem 0.85rem",
              marginBottom: "0.85rem",
              fontSize: "0.85rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              justifyContent: "space-between",
            }}
          >
            <span>
              <strong>✓ Code saved:</strong> <code style={{ background: "rgba(0,0,0,0.25)", padding: "2px 6px", borderRadius: "4px" }}>{lastCreatedCode}</code>
            </span>
            <button
              onClick={() => { copyCode(lastCreatedCode); }}
              data-testid="admin-create-success-copy"
              style={{ background: "transparent", color: "inherit", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "6px", padding: "2px 10px", fontSize: "0.75rem", cursor: "pointer" }}
            >
              {copiedCode === lastCreatedCode ? "✓ Copied" : "Copy"}
            </button>
          </div>
        )}

        {createOpen && (
          <div className="admin-promo-form" data-testid="admin-promo-form">
            <div className="admin-promo-tabs">
              <button
                className={`admin-promo-tab ${mode === "auto" ? "active" : ""}`}
                onClick={() => setMode("auto")}
                data-testid="mode-auto-btn"
              >Auto-generate</button>
              <button
                className={`admin-promo-tab ${mode === "custom" ? "active" : ""}`}
                onClick={() => setMode("custom")}
                data-testid="mode-custom-btn"
              >Custom</button>
            </div>

            {mode === "custom" && (
              <input
                className="init-input"
                placeholder="MOMBDAY2026"
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                data-testid="custom-code-input"
              />
            )}

            <label className="admin-label">Reward</label>
            <select
              className="init-input"
              value={rewardType}
              onChange={(e) => setRewardType(e.target.value)}
              data-testid="reward-type-select"
            >
              <option value="extended_trial">Extended trial (extra days)</option>
              <option value="free_base">Free Base tier ($4.99)</option>
              <option value="free_premium_month">Free month(s) of Premium</option>
              <option value="free_topup_minutes">Free Aura voice minutes</option>
              <option value="free_creator_studio_month">Free Creator Studio month(s)</option>
              <option value="percent_off">% off at checkout (Stripe discount)</option>
            </select>

            {rewardType === "extended_trial" && (
              <>
                <label className="admin-label">Trial days</label>
                <input
                  className="init-input"
                  type="number"
                  min="1"
                  max="365"
                  value={trialDays}
                  onChange={(e) => setTrialDays(e.target.value)}
                  data-testid="trial-days-input"
                />
              </>
            )}

            {rewardType === "free_premium_month" && (
              <>
                <label className="admin-label">Which tier?</label>
                <select
                  className="init-input"
                  value={freeMonthTier}
                  onChange={(e) => setFreeMonthTier(e.target.value)}
                  data-testid="free-month-tier-select"
                >
                  {PREMIUM_TIER_OPTIONS.map((t) => (
                    <option key={t.slug} value={t.slug}>{t.label}</option>
                  ))}
                </select>
                <label className="admin-label">Duration</label>
                <select
                  className="init-input"
                  value={premiumMonths}
                  onChange={(e) => setPremiumMonths(parseInt(e.target.value, 10) || 1)}
                  data-testid="premium-months-select"
                >
                  <option value={1}>1 month</option>
                  <option value={3}>3 months</option>
                  <option value={6}>6 months</option>
                  <option value={12}>1 year (12 months)</option>
                </select>
                <div className="admin-promo-hint" data-testid="premium-months-hint">
                  {premiumMonths === 12
                    ? "💎 Best for influencer giveaways + lifetime-feel rewards."
                    : premiumMonths >= 3
                      ? "✨ Multi-month promos stack on top of existing premium time."
                      : "Standard 1-month gift — same as before."}
                </div>
              </>
            )}

            {rewardType === "free_topup_minutes" && (
              <>
                <label className="admin-label">Minute pack</label>
                <select
                  className="init-input"
                  value={topupMinutes}
                  onChange={(e) => setTopupMinutes(parseInt(e.target.value, 10) || 30)}
                  data-testid="topup-minutes-select"
                >
                  {TOPUP_MINUTE_OPTIONS.map((m) => (
                    <option key={m} value={m}>{m} minutes</option>
                  ))}
                </select>
                <div className="admin-promo-hint" data-testid="topup-minutes-hint">
                  🎙️ Credits the redeemer's voice balance — works on any tier, including free trial.
                </div>
              </>
            )}

            {rewardType === "free_creator_studio_month" && (
              <>
                <label className="admin-label">Months of Creator Studio</label>
                <select
                  className="init-input"
                  value={creatorStudioMonths}
                  onChange={(e) => setCreatorStudioMonths(parseInt(e.target.value, 10) || 1)}
                  data-testid="creator-studio-months-select"
                >
                  {CREATOR_STUDIO_MONTH_OPTIONS.map((m) => (
                    <option key={m} value={m}>
                      {m === 12 ? "1 year" : m === 1 ? "1 month" : `${m} months`}
                    </option>
                  ))}
                </select>
                <div className="admin-promo-hint" data-testid="creator-studio-months-hint">
                  🎬 Gifts Creator Studio access — no Stripe charge. Stacks on top of existing access.
                </div>
              </>
            )}

            {rewardType === "percent_off" && (
              <>
                <label className="admin-label">Discount percentage</label>
                <input
                  className="init-input"
                  type="number"
                  min="1"
                  max="99"
                  value={percentOff}
                  onChange={(e) => setPercentOff(e.target.value)}
                  data-testid="percent-off-input"
                  placeholder="e.g. 25"
                />
                <label className="admin-label">Applies to</label>
                <select
                  className="init-input"
                  value={percentAppliesTo}
                  onChange={(e) => setPercentAppliesTo(e.target.value)}
                  data-testid="percent-applies-to-select"
                >
                  {PERCENT_APPLIES_OPTIONS.map((o) => (
                    <option key={o.slug} value={o.slug}>{o.label}</option>
                  ))}
                </select>
                <div className="admin-promo-hint" data-testid="percent-off-hint">
                  💸 Real Stripe discount — auto-creates a Stripe Coupon + Promotion Code.
                  Users enter the code in the "Add promotion code" field on the Stripe checkout
                  page and the {percentOff || "X"}% discount applies automatically. First-charge
                  only (one-time discount). Deactivating or deleting the code here disables it
                  in Stripe instantly.
                </div>
              </>
            )}

            <label className="admin-label">Max uses (blank = unlimited)</label>
            <input
              className="init-input"
              type="number"
              min="1"
              placeholder="unlimited"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              data-testid="max-uses-input"
            />

            <label className="admin-label">Expires (optional)</label>
            <input
              className="init-input"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              data-testid="expires-input"
            />

            <label className="admin-label">Note (admin-only memo)</label>
            <input
              className="init-input"
              placeholder="e.g. Instagram ad #3"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="note-input"
            />

            {createError && <div className="error-message">{createError}</div>}

            <button
              className="init-btn"
              onClick={handleCreate}
              disabled={creating}
              data-testid="admin-create-submit"
            >
              {creating ? "Creating..." : "Create code"}
            </button>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="admin-promo-list">
          {loading ? (
            <p style={{ opacity: 0.7 }}>Loading...</p>
          ) : promos.length === 0 ? (
            <p style={{ opacity: 0.7 }}>No codes yet. Create one to get started.</p>
          ) : (
            promos.map((p) => {
              const exhausted = p.max_uses != null && p.used_count >= p.max_uses;
              return (
                <div
                  key={p.code}
                  className={`admin-promo-row ${!p.is_active ? "inactive" : ""} ${exhausted ? "exhausted" : ""}`}
                  data-testid={`promo-row-${p.code}`}
                >
                  <div className="admin-promo-row-head">
                    <code className="admin-promo-code">{p.code}</code>
                    <button
                      className="admin-promo-copy"
                      onClick={() => copyCode(p.code)}
                      data-testid={`copy-${p.code}`}
                    >{copiedCode === p.code ? "✓ Copied" : "Copy"}</button>
                  </div>
                  <div className="admin-promo-meta">
                    <span>
                      {p.reward_type === "free_premium_month"
                        ? (() => {
                            const m = parseInt(p.months || 1, 10);
                            const tierName = TIER_NAMES[p.tier] || "Spark";
                            if (m === 12) return `1 free YEAR of ${tierName}`;
                            if (m === 1) return `1 free month of ${tierName}`;
                            return `${m} free months of ${tierName}`;
                          })()
                        : p.reward_type === "free_topup_minutes"
                          ? `${p.topup_minutes || 30} free Aura voice minutes`
                          : p.reward_type === "free_creator_studio_month"
                            ? (() => {
                                const m = parseInt(p.creator_studio_months || 1, 10);
                                if (m === 12) return "1 free YEAR of Creator Studio";
                                if (m === 1) return "1 free month of Creator Studio";
                                return `${m} free months of Creator Studio`;
                              })()
                            : p.reward_type === "percent_off"
                              ? `${p.percent || "?"}% off ${p.applies_to === "subscriptions" ? "subscriptions" : "any purchase"}`
                              : REWARD_LABELS[p.reward_type]}
                      {p.reward_type === "extended_trial" ? ` (${p.value} days)` : ""}
                    </span>
                    <span>·</span>
                    <span>{p.used_count}/{p.max_uses ?? "∞"} used</span>
                    {p.expires_at && <><span>·</span><span>exp {p.expires_at.slice(0, 10)}</span></>}
                  </div>
                  {p.note && <div className="admin-promo-note">{p.note}</div>}
                  <div className="admin-promo-actions">
                    <button
                      className="admin-action-btn"
                      onClick={() => handleExpand(p.code)}
                      data-testid={`expand-${p.code}`}
                    >{expandedCode === p.code ? "Hide" : "Who used?"}</button>
                    <button
                      className="admin-action-btn"
                      onClick={() => handleToggle(p.code)}
                      data-testid={`toggle-${p.code}`}
                    >{p.is_active ? "Deactivate" : "Activate"}</button>
                    <button
                      className="admin-action-btn danger"
                      onClick={() => handleDelete(p.code)}
                      data-testid={`delete-${p.code}`}
                    >Delete</button>
                  </div>
                  {expandedCode === p.code && (
                    <div className="admin-promo-redemptions" data-testid={`redemptions-${p.code}`}>
                      {(redemptions[p.code] || []).length === 0 ? (
                        <p style={{ opacity: 0.6, fontSize: "0.75rem" }}>No redemptions yet.</p>
                      ) : (
                        (redemptions[p.code] || []).map((r) => (
                          <div key={r.id} className="admin-redemption-row">
                            <span>{r.user_name || r.user_email}</span>
                            <span style={{ opacity: 0.6 }}>{(r.redeemed_at || "").slice(0, 10)}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
